import clsx from 'clsx';
import type React from 'react';
import { Button, DropZone, FileTrigger, Text } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import {
    DOCX_FILE_EXTENSIONS,
    DOCX_MIME_TYPES,
    HTML_FILE_EXTENSIONS,
    HTML_MIME_TYPES,
    MARKDOWN_FILE_EXTENSIONS,
    MARKDOWN_MIME_TYPES,
    ZIP_FILE_EXTENSIONS,
    ZIP_MIME_TYPES,
} from '@gitbook/util-files';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { getPlaceholderStyles } from '../PlaceholderButton';
import { type IconDefinition, renderIcon } from '../util';

export type FilesDropZoneKind = 'default' | 'primary';

export type FilesDropZoneAccept =
    | 'files'
    | 'images'
    | 'png-jpg-images'
    | 'openapi'
    | 'drawings'
    | 'fonts'
    | 'csv'
    | 'markdown|html|docx|zip';

export interface FilesDropZoneProps {
    /**
     * File types to accept.
     */
    acceptedFileTypes?: FilesDropZoneAccept;

    /**
     * Callback when files are selected.
     */
    onSelectFiles: (files: File[]) => void;

    /**
     * Text to display in the drop zone.
     */
    children?: React.ReactNode;

    /**
     * Custom icon to display in the drop zone.
     */
    icon?: IconDefinition;

    label?: string;
    disabled?: boolean;
}

/**
 * Drop zone where the user can drop files or press it to select files.
 */
export function FilesDropZone(props: FilesDropZoneProps) {
    const {
        acceptedFileTypes = 'files',
        icon = getIcon(props.acceptedFileTypes ?? 'files'),
        onSelectFiles,
        children,
    } = props;

    const fileTypes = getFileTypes(acceptedFileTypes);
    return (
        <DropZone
            className="group/dropzone flex w-full"
            getDropOperation={
                // Do it only for openapi in the meantime to avoid regressions.
                acceptedFileTypes === 'openapi' && fileTypes
                    ? (types) => {
                          return fileTypes.some((type) => types.has(type)) ? 'copy' : 'cancel';
                      }
                    : undefined
            }
            onDrop={async (event) => {
                const files = (
                    await Promise.all(
                        event.items.map((item) => (item.kind === 'file' ? item.getFile() : null))
                    )
                ).filter(filterOutNullable);
                onSelectFiles(files);
            }}
            isDisabled={props.disabled}
        >
            <FileTrigger
                allowsMultiple
                acceptedFileTypes={getFileTypes(acceptedFileTypes)}
                onSelect={(fileList) => {
                    if (!fileList) {
                        return;
                    }

                    const files = Array.from(fileList);
                    onSelectFiles(files);
                }}
            >
                <Button
                    className={(renderProps) =>
                        clsx(
                            getPlaceholderStyles(renderProps),
                            'flex flex-1 cursor-pointer items-center justify-center gap-4 rounded-sm border p-5 text-ui-action transition-all',
                            'group-data-[drop-target]/dropzone:border-success group-data-[drop-target]/dropzone:bg-success group-data-[drop-target]/dropzone:text-success'
                        )
                    }
                    isDisabled={props.disabled}
                >
                    {children ?? (
                        <>
                            {renderIcon(icon, { className: 'size-4' })}
                            <Text slot="label">
                                {props.label ?? (
                                    <>Drop your {getFileLabel(acceptedFileTypes)} or browse</>
                                )}
                            </Text>
                        </>
                    )}
                </Button>
            </FileTrigger>
        </DropZone>
    );
}

function getFileTypes(acceptedFileTypes: FilesDropZoneAccept): string[] | undefined {
    switch (acceptedFileTypes) {
        case 'drawings':
            return ['image/svg+xml', '.svg'];
        case 'images':
            return ['image/*'];
        case 'openapi':
            return ['application/json', 'application/x-yaml', 'text/yaml'];
        case 'png-jpg-images':
            return ['image/png', 'image/jpeg'];
        case 'fonts':
            return ['font/woff', 'font/woff2', '.woff', '.woff2'];
        case 'csv':
            return ['text/csv', '.csv'];
        case 'markdown|html|docx|zip':
            return [
                ...MARKDOWN_MIME_TYPES,
                ...MARKDOWN_FILE_EXTENSIONS,
                ...HTML_MIME_TYPES,
                ...HTML_FILE_EXTENSIONS,
                ...DOCX_MIME_TYPES,
                ...DOCX_FILE_EXTENSIONS,
                ...ZIP_MIME_TYPES,
                ...ZIP_FILE_EXTENSIONS,
            ];
        case 'files':
            return;
        default:
            assertNever(acceptedFileTypes);
    }
}

function getFileLabel(acceptedFileTypes: FilesDropZoneAccept) {
    switch (acceptedFileTypes) {
        case 'drawings':
            return 'SVG drawing files';
        case 'images':
            return 'images';
        case 'openapi':
            return 'OpenAPI files';
        case 'fonts':
            return 'font files';
        case 'csv':
            return 'CSV files';
        case 'markdown|html|docx|zip':
            return 'Markdown, HTML, DOCX, or ZIP files';
        default:
            return 'files';
    }
}

function getIcon(acceptedFileTypes: FilesDropZoneAccept) {
    switch (acceptedFileTypes) {
        case 'drawings':
        case 'images':
            return Icon.Image;
        case 'openapi':
            return Icon.OpenAPI;
        case 'csv':
            return Icon.Table;
        default:
            return Icon.Files;
    }
}
